export type AppEnvironment = 'development' | 'sandbox' | 'uat' | 'production';

export type LogLevel = 'debug' | 'info' | 'warning' | 'error' | 'critical';

export interface EnvironmentConfig {
  env: AppEnvironment;
  isDevelopment: boolean;
  isSandbox: boolean;
  isUAT: boolean;
  isProduction: boolean;
  developerModeEnabled: boolean;
  bypassSubscriptionChecks: boolean;
  showDeveloperTools: boolean;
  enableMockData: boolean;
  logLevel: LogLevel;
}

class EnvironmentService {
  private config: EnvironmentConfig;

  constructor() {
    const env = (import.meta.env.VITE_APP_ENV || 'development') as AppEnvironment;
    const developerModeEnabled = import.meta.env.VITE_ENABLE_DEVELOPER_MODE === 'true';
    const logLevel = (import.meta.env.VITE_LOG_LEVEL || 'info') as LogLevel;

    this.config = {
      env,
      isDevelopment: env === 'development',
      isSandbox: env === 'sandbox',
      isUAT: env === 'uat',
      isProduction: env === 'production',
      developerModeEnabled: developerModeEnabled && (env === 'development' || env === 'sandbox'),
      bypassSubscriptionChecks: import.meta.env.VITE_BYPASS_SUBSCRIPTION_CHECKS === 'true',
      showDeveloperTools: import.meta.env.VITE_SHOW_DEVELOPER_TOOLS === 'true',
      enableMockData: import.meta.env.VITE_ENABLE_MOCK_DATA === 'true',
      logLevel,
    };
  }

  getConfig(): EnvironmentConfig {
    return { ...this.config };
  }

  getEnvironment(): AppEnvironment {
    return this.config.env;
  }

  isDeveloperMode(): boolean {
    return this.config.developerModeEnabled;
  }

  shouldBypassSubscriptionChecks(): boolean {
    return this.config.bypassSubscriptionChecks && this.config.developerModeEnabled;
  }

  shouldEnforceSubscription(): boolean {
    return !this.shouldBypassSubscriptionChecks();
  }

  canShowDeveloperTools(): boolean {
    return this.config.showDeveloperTools && this.config.developerModeEnabled;
  }

  getLogLevel(): LogLevel {
    return this.config.logLevel;
  }

  shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warning', 'error', 'critical'];
    const currentLevelIndex = levels.indexOf(this.config.logLevel);
    const messageLevel = levels.indexOf(level);
    return messageLevel >= currentLevelIndex;
  }

  isProductionLike(): boolean {
    return this.config.isUAT || this.config.isProduction;
  }

  isProduction(): boolean {
    return this.config.isProduction;
  }

  getEnvironmentLabel(): string {
    switch (this.config.env) {
      case 'development':
        return 'Development';
      case 'sandbox':
        return 'Sandbox';
      case 'uat':
        return 'UAT';
      case 'production':
        return 'Production';
      default:
        return 'Unknown';
    }
  }

  getEnvironmentColor(): string {
    switch (this.config.env) {
      case 'development':
        return '#10b981';
      case 'sandbox':
        return '#3b82f6';
      case 'uat':
        return '#f59e0b';
      case 'production':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  }
}

export const environmentService = new EnvironmentService();
